import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Clock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface StatCardProps {
  title: string;
  value: string | number;
  description?: string;
  tooltipContent?: string | React.ReactNode;
  icon?: React.ReactNode;
  change?: number;
  usdValue?: number;
  showUpdateCountdown?: boolean;
  lastUpdated?: Date;
  className?: string;
}

const formatLargeNumber = (value: number): string => {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1)}M`;
  } else if (value >= 1_000) {
    return `${(value / 1_000).toFixed(1)}K`;
  }
  return value.toString();
};

export function StatCard({ 
  title, 
  value, 
  description, 
  tooltipContent, 
  icon, 
  change, 
  usdValue,
  showUpdateCountdown,
  lastUpdated,
  className 
}: StatCardProps) {
  const [countdown, setCountdown] = useState<number>(0);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (!showUpdateCountdown || !lastUpdated) return;

    const updateCountdown = () => {
      const now = new Date().getTime();
      const updated = new Date(lastUpdated).getTime();
      const diff = Math.floor((now - updated) / 1000);
      setCountdown(diff);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [showUpdateCountdown, lastUpdated]);

  const formatValue = (val: string | number): string => {
    if (typeof val === 'number' && isMobile) {
      return formatLargeNumber(val);
    }
    return val.toString();
  };

  const displayValue = formatValue(value);
  const formattedUsdValue = usdValue ? 
    new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      notation: isMobile ? 'compact' : 'standard',
      maximumFractionDigits: 0
    }).format(usdValue) : null;

  const isRankCard = title === "Node Ranking";
  const rankValue = isRankCard ? displayValue.split('/') : null;

  const cardContent = (
    <Card className={className}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-1 sm:pb-2">
        <CardTitle className="text-sm font-medium">
          {title}
        </CardTitle>
        {icon && <div className={`${isRankCard ? 'text-dusk' : 'text-muted-foreground'}`}>{icon}</div>}
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col">
          {isRankCard ? (
            <div className="flex items-baseline gap-1">
              <span className="text-xl sm:text-2xl font-bold text-dusk">{rankValue?.[0]}</span>
              <span className="text-xs sm:text-sm text-muted-foreground">of {rankValue?.[1]}</span>
            </div>
          ) : (
            <div className="text-xl sm:text-2xl font-bold">{displayValue}</div>
          )}
          {formattedUsdValue && (
            <div className="text-xs text-yellow-500 font-medium">
              {formattedUsdValue}
            </div>
          )}
          {description && (
            <p className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {typeof change === 'number' && (
            <div className={`flex items-center text-xs ${
              change > 0 ? 'text-green-500' : change < 0 ? 'text-red-500' : 'text-gray-500'
            }`}>
              {change > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> : 
               change < 0 ? <TrendingDown className="h-4 w-4 mr-1" /> : null}
              {change > 0 ? '+' : ''}{change}%
            </div>
          )}
          {showUpdateCountdown && lastUpdated && (
            <div className="flex items-center text-xs text-muted-foreground mt-1">
              <Clock className="h-3 w-3 mr-1" />
              {countdown}s ago
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  if (tooltipContent) {
    return (
      <Tooltip>
        <TooltipTrigger asChild className="w-full">
          <div className="cursor-help w-full">
            {cardContent}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{tooltipContent}</p>
        </TooltipContent>
      </Tooltip>
    );
  }

  return cardContent;
}